import { Component, type ErrorInfo, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";

/**
 * Without this, ANY error thrown while rendering (one unexpected record shape
 * on one card) unmounted the whole app: a blank white page for that user,
 * every time they opened it. A crash now stays inside the boundary that
 * caught it, shows a way out, and is logged to the console for diagnosis.
 */
interface Props {
  children: ReactNode;
  /** Changing it clears a caught error (the page boundary passes the route). */
  resetKey?: string;
  /** "page": inside a portal layout (sidebar stays). "app": the last resort. */
  variant?: "page" | "app";
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] a screen crashed while rendering", error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const page = this.props.variant !== "app";
    return (
      <div
        role="alert"
        data-testid="error-boundary"
        className={page ? "flex min-h-[50vh] items-center justify-center p-6" : "flex min-h-screen items-center justify-center bg-[#F9FAFB] p-6"}
      >
        <div className="max-w-md rounded-2xl border border-[#E5E7EB] bg-white p-6 text-center shadow-sm">
          <p className="text-lg font-extrabold text-[#0A2947]">Something went wrong on this page</p>
          <p className="mt-2 text-sm text-[#4B5563]">
            Your records are safe. Reload to try again{page ? ", or open another page from the menu" : ""}.
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="h-10 rounded-lg !border-transparent !bg-[#E4002B]/50 px-5 text-sm font-semibold !text-white hover:!bg-[#E4002B]/60 hover:!text-white"
            >
              Reload
            </button>
            {!page && (
              <button
                type="button"
                onClick={() => window.location.assign("/")}
                className="h-10 rounded-lg border border-[#E5E7EB] bg-white px-5 text-sm font-semibold text-[#0A2947]"
              >
                Go to home
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

/** A layout's <Outlet /> behind a page boundary that clears on navigation. */
export function SafeOutlet() {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname}>
      <Outlet />
    </ErrorBoundary>
  );
}
